import React, { useEffect, useMemo, useState, useRef } from 'react';
import { Card, CardHeader, CardTitle, CardContent } from './ui/Card';
import { Button } from './ui/Button';
import { Badge } from './ui/Badge';
import { PhoneIcon, MailIcon, SearchIcon, XIcon, CheckIcon, ChevronDownIcon, UserPlusIcon, CheckCircleIcon, ArrowUpDownIcon, ArrowUpIcon, ArrowDownIcon } from 'lucide-react';
interface Borrower {
  id: string;
  name: string;
  loanAmount: number;
  rate: number;
  fundedDate: string;
  status: 'unclaimed' | 'contacted' | 'funded';
  phone: string;
  email: string;
  propertyValue: number;
  loanTerm: number;
  loanType: 'Conventional' | 'FHA' | 'VA' | 'Jumbo';
  claimedAt?: number;
  lastContactAttempt?: string;
}
const INACTIVE_BORROWERS: Borrower[] = [{
  id: '101',
  name: 'William Taylor',
  loanAmount: 380000,
  rate: 6.99,
  fundedDate: '2023-08-12',
  status: 'unclaimed',
  phone: '(555) 222-3333',
  email: 'w.taylor@example.com',
  propertyValue: 450000,
  loanTerm: 30,
  loanType: 'Conventional',
  lastContactAttempt: '2025-01-20T10:30:00'
}, {
  id: '102',
  name: 'Sophia Martinez',
  loanAmount: 520000,
  rate: 7.25,
  fundedDate: '2023-10-05',
  status: 'unclaimed',
  phone: '(555) 444-5555',
  email: 's.martinez@example.com',
  propertyValue: 610000,
  loanTerm: 30,
  loanType: 'FHA',
  lastContactAttempt: '2025-02-14T14:15:00'
}, {
  id: '103',
  name: 'Daniel White',
  loanAmount: 780000,
  rate: 6.5,
  fundedDate: '2023-05-20',
  status: 'unclaimed',
  phone: '(555) 666-7777',
  email: 'd.white@example.com',
  propertyValue: 950000,
  loanTerm: 30,
  loanType: 'Jumbo'
}, {
  id: '104',
  name: 'Olivia Harris',
  loanAmount: 295000,
  rate: 7.125,
  fundedDate: '2023-11-18',
  status: 'unclaimed',
  phone: '(555) 888-9999',
  email: 'o.harris@example.com',
  propertyValue: 340000,
  loanTerm: 30,
  loanType: 'VA',
  lastContactAttempt: '2025-03-01T09:00:00'
}, {
  id: '105',
  name: 'Alexander Clark',
  loanAmount: 410000,
  rate: 6.875,
  fundedDate: '2023-09-30',
  status: 'unclaimed',
  phone: '(555) 111-2222',
  email: 'a.clark@example.com',
  propertyValue: 520000,
  loanTerm: 30,
  loanType: 'Conventional',
  lastContactAttempt: '2024-12-18T16:45:00'
}, {
  id: '106',
  name: 'Mia Lewis',
  loanAmount: 650000,
  rate: 7.5,
  fundedDate: '2023-07-14',
  status: 'unclaimed',
  phone: '(555) 333-4444',
  email: 'm.lewis@example.com',
  propertyValue: 800000,
  loanTerm: 30,
  loanType: 'Jumbo'
}, {
  id: '107',
  name: 'Ethan Walker',
  loanAmount: 310000,
  rate: 6.375,
  fundedDate: '2023-04-22',
  status: 'unclaimed',
  phone: '(555) 555-6666',
  email: 'e.walker@example.com',
  propertyValue: 400000,
  loanTerm: 30,
  loanType: 'Conventional',
  lastContactAttempt: '2025-02-05T11:20:00'
}, {
  id: '108',
  name: 'Isabella Hall',
  loanAmount: 240000,
  rate: 7.375,
  fundedDate: '2023-12-08',
  status: 'unclaimed',
  phone: '(555) 777-8888',
  email: 'i.hall@example.com',
  propertyValue: 290000,
  loanTerm: 30,
  loanType: 'FHA',
  lastContactAttempt: '2025-01-10T13:00:00'
}, {
  id: '109',
  name: 'Matthew Young',
  loanAmount: 480000,
  rate: 6.75,
  fundedDate: '2023-06-11',
  status: 'unclaimed',
  phone: '(555) 999-0000',
  email: 'm.young@example.com',
  propertyValue: 600000,
  loanTerm: 30,
  loanType: 'Conventional'
}, {
  id: '110',
  name: 'Charlotte King',
  loanAmount: 590000,
  rate: 7.0,
  fundedDate: '2023-08-25',
  status: 'unclaimed',
  phone: '(555) 234-5678',
  email: 'c.king@example.com',
  propertyValue: 750000,
  loanTerm: 30,
  loanType: 'VA',
  lastContactAttempt: '2025-03-05T08:30:00'
}, {
  id: '111',
  name: 'Benjamin Wright',
  loanAmount: 350000,
  rate: 6.625,
  fundedDate: '2023-05-05',
  status: 'unclaimed',
  phone: '(555) 345-6789',
  email: 'b.wright@example.com',
  propertyValue: 440000,
  loanTerm: 30,
  loanType: 'Conventional'
}, {
  id: '112',
  name: 'Amelia Scott',
  loanAmount: 195000,
  rate: 7.75,
  fundedDate: '2023-11-29',
  status: 'unclaimed',
  phone: '(555) 456-7890',
  email: 'a.scott@example.com',
  propertyValue: 250000,
  loanTerm: 30,
  loanType: 'FHA',
  lastContactAttempt: '2025-02-22T15:40:00'
}];
function formatUSD(amount: number) {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0
  }).format(amount);
}
interface FilterOption {
  value: string;
  label: string;
}
function MultiSelectFilter({
  label,
  options,
  selected,
  onChange
}: {
  label: string;
  options: FilterOption[];
  selected: Set<string>;
  onChange: (selected: Set<string>) => void;
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    if (open) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [open]);
  const toggle = (value: string) => {
    const next = new Set(selected);
    if (next.has(value)) {
      next.delete(value);
    } else {
      next.add(value);
    }
    onChange(next);
  };
  const activeCount = selected.size;
  const displayLabel = activeCount === 0 ? label : `${label} (${activeCount})`;
  return <div className="relative" ref={ref} data-id="element-141">
      <button onClick={() => setOpen(!open)} className={`h-9 pl-3 pr-2.5 rounded-lg border text-sm flex items-center gap-1.5 cursor-pointer transition-all whitespace-nowrap ${activeCount > 0 ? 'border-blue-300 bg-blue-50 text-blue-700 ring-1 ring-blue-200' : 'border-gray-200 bg-white text-gray-700 hover:border-gray-300'}`} data-id="element-142">
        {displayLabel}
        <ChevronDownIcon className={`w-3.5 h-3.5 transition-transform ${open ? 'rotate-180' : ''}`} data-id="element-143" />
      </button>

      {open && <div className="absolute top-full left-0 mt-1.5 bg-white rounded-lg border border-gray-200 shadow-lg z-30 min-w-[180px] py-1.5 animate-in fade-in slide-in-from-top-1" data-id="element-144">
          {options.map(option => {
        const isSelected = selected.has(option.value);
        return <button key={option.value} onClick={() => toggle(option.value)} className={`w-full flex items-center gap-2.5 px-3 py-2 text-sm text-left transition-colors ${isSelected ? 'text-blue-700 bg-blue-50/50' : 'text-gray-700 hover:bg-gray-50'}`} data-id="element-145">
                <span className={`w-4 h-4 rounded border flex items-center justify-center flex-shrink-0 transition-colors ${isSelected ? 'bg-blue-600 border-blue-600' : 'border-gray-300 bg-white'}`} data-id="element-146">
                  {isSelected && <CheckIcon className="w-3 h-3 text-white" data-id="element-147" />}
                </span>
                {option.label}
              </button>;
      })}
          {activeCount > 0 && <>
              <div className="border-t border-gray-100 my-1" data-id="element-148"></div>
              <button onClick={() => onChange(new Set())} className="w-full flex items-center gap-2.5 px-3 py-2 text-sm text-gray-500 hover:text-gray-700 hover:bg-gray-50 transition-colors" data-id="element-149">
                <XIcon className="w-3.5 h-3.5" data-id="element-150" />
                Clear selection
              </button>
            </>}
        </div>}
    </div>;
}
const RATE_OPTIONS: FilterOption[] = [{
  value: 'above-7',
  label: 'Above 7%'
}, {
  value: '6.5-7',
  label: '6.5% – 7%'
}, {
  value: 'below-6.5',
  label: 'Below 6.5%'
}];
const AMOUNT_OPTIONS: FilterOption[] = [{
  value: 'under-300k',
  label: 'Under $300K'
}, {
  value: '300k-500k',
  label: '$300K – $500K'
}, {
  value: 'over-500k',
  label: 'Over $500K'
}];
const DATE_OPTIONS: FilterOption[] = [{
  value: 'last-3',
  label: 'Last 3 months'
}, {
  value: '3-6',
  label: '3–6 months ago'
}, {
  value: 'over-6',
  label: '6+ months ago'
}];
const TYPE_OPTIONS: FilterOption[] = [{
  value: 'Conventional',
  label: 'Conventional'
}, {
  value: 'FHA',
  label: 'FHA'
}, {
  value: 'VA',
  label: 'VA'
}, {
  value: 'Jumbo',
  label: 'Jumbo'
}];
type SortKey = 'name' | 'phone' | 'email' | 'loanAmount' | 'loanType' | 'rate' | 'fundedDate' | 'lastContactAttempt';
type SortDir = 'asc' | 'desc';
interface InactiveOwnerPoolProps {
  onCountChange?: (count: number) => void;
  onClaim?: (borrower: Borrower) => void;
}
export function InactiveOwnerPool({
  onCountChange,
  onClaim
}: InactiveOwnerPoolProps) {
  const [borrowers, setBorrowers] = useState<Borrower[]>(INACTIVE_BORROWERS);
  const [claimingId, setClaimingId] = useState<string | null>(null);
  const [sortKey, setSortKey] = useState<SortKey | null>(null);
  const [sortDir, setSortDir] = useState<SortDir>('asc');
  // Filter states
  const [searchQuery, setSearchQuery] = useState('');
  const [rateFilters, setRateFilters] = useState<Set<string>>(new Set());
  const [amountFilters, setAmountFilters] = useState<Set<string>>(new Set());
  const [dateFilters, setDateFilters] = useState<Set<string>>(new Set());
  const [loanTypeFilters, setLoanTypeFilters] = useState<Set<string>>(new Set());
  const handleSort = (key: SortKey) => {
    if (sortKey === key) {
      if (sortDir === 'asc') setSortDir('desc');else {
        setSortKey(null);
        setSortDir('asc');
      }
    } else {
      setSortKey(key);
      setSortDir('asc');
    }
  };
  const filteredBorrowers = useMemo(() => {
    return borrowers.filter(borrower => {
      if (searchQuery && !borrower.name.toLowerCase().includes(searchQuery.toLowerCase())) {
        return false;
      }
      if (rateFilters.size > 0) {
        const matchesRate = Array.from(rateFilters).some(filter => {
          if (filter === 'above-7') return borrower.rate > 7;
          if (filter === '6.5-7') return borrower.rate >= 6.5 && borrower.rate <= 7;
          if (filter === 'below-6.5') return borrower.rate < 6.5;
          return false;
        });
        if (!matchesRate) return false;
      }
      if (amountFilters.size > 0) {
        const matchesAmount = Array.from(amountFilters).some(filter => {
          if (filter === 'under-300k') return borrower.loanAmount < 300000;
          if (filter === '300k-500k') return borrower.loanAmount >= 300000 && borrower.loanAmount <= 500000;
          if (filter === 'over-500k') return borrower.loanAmount > 500000;
          return false;
        });
        if (!matchesAmount) return false;
      }
      if (dateFilters.size > 0) {
        const fundedDate = new Date(borrower.fundedDate);
        const now = new Date();
        const monthsDiff = (now.getFullYear() - fundedDate.getFullYear()) * 12 + (now.getMonth() - fundedDate.getMonth());
        const matchesDate = Array.from(dateFilters).some(filter => {
          if (filter === 'last-3') return monthsDiff <= 3;
          if (filter === '3-6') return monthsDiff > 3 && monthsDiff <= 6;
          if (filter === 'over-6') return monthsDiff > 6;
          return false;
        });
        if (!matchesDate) return false;
      }
      if (loanTypeFilters.size > 0) {
        if (!loanTypeFilters.has(borrower.loanType)) return false;
      }
      return true;
    });
  }, [borrowers, searchQuery, rateFilters, amountFilters, dateFilters, loanTypeFilters]);
  const sortedBorrowers = useMemo(() => {
    if (!sortKey) return filteredBorrowers;
    const sorted = [...filteredBorrowers].sort((a, b) => {
      let cmp = 0;
      switch (sortKey) {
        case 'name':
        case 'phone':
        case 'email':
        case 'loanType':
          cmp = (a[sortKey] || '').localeCompare(b[sortKey] || '');
          break;
        case 'loanAmount':
        case 'rate':
          cmp = a[sortKey] - b[sortKey];
          break;
        case 'fundedDate':
          cmp = new Date(a.fundedDate).getTime() - new Date(b.fundedDate).getTime();
          break;
        case 'lastContactAttempt':
          const aTime = a.lastContactAttempt ? new Date(a.lastContactAttempt).getTime() : 0;
          const bTime = b.lastContactAttempt ? new Date(b.lastContactAttempt).getTime() : 0;
          cmp = aTime - bTime;
          break;
      }
      return sortDir === 'desc' ? -cmp : cmp;
    });
    return sorted;
  }, [filteredBorrowers, sortKey, sortDir]);
  const clearFilters = () => {
    setSearchQuery('');
    setRateFilters(new Set());
    setAmountFilters(new Set());
    setDateFilters(new Set());
    setLoanTypeFilters(new Set());
  };
  const hasActiveFilters = searchQuery || rateFilters.size > 0 || amountFilters.size > 0 || dateFilters.size > 0 || loanTypeFilters.size > 0;
  const handleClaim = (id: string) => {
    setClaimingId(id);
    setTimeout(() => {
      setBorrowers(prev => {
        const claimedBorrower = prev.find(b => b.id === id);
        const next = prev.filter(b => b.id !== id);
        onCountChange?.(next.length);
        if (claimedBorrower && onClaim) {
          onClaim({
            ...claimedBorrower,
            claimedAt: Date.now()
          });
        }
        return next;
      });
      setClaimingId(null);
    }, 800);
  };
  const SortIcon = ({
    column
  }: {
    column: SortKey;
  }) => {
    if (sortKey !== column) return <ArrowUpDownIcon className="w-3 h-3 text-gray-400 ml-1 inline-block" data-id="element-151" />;
    if (sortDir === 'asc') return <ArrowUpIcon className="w-3 h-3 text-blue-600 ml-1 inline-block" data-id="element-152" />;
    return <ArrowDownIcon className="w-3 h-3 text-blue-600 ml-1 inline-block" data-id="element-153" />;
  };
  return <Card className="h-full flex flex-col" data-id="element-154">
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-4" data-id="element-155">
        <div data-id="element-156">
          <CardTitle data-id="element-157">Inactive Owner Pool</CardTitle>
          <p className="text-sm text-gray-600 mt-1" data-id="element-158">
            {filteredBorrowers.length} unclaimed borrowers eligible for
            follow-up
          </p>
        </div>
      </CardHeader>

      {/* Filter Bar */}
      <div className="px-6 py-3 border-b border-gray-200 bg-gray-50/50 flex flex-col md:flex-row gap-3 items-start md:items-center" data-id="element-159">
        <div className="relative w-full md:w-56 flex-shrink-0" data-id="element-160">
          <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" data-id="element-161" />
          <input type="text" placeholder="Search by name..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className="w-full h-9 pl-9 pr-4 rounded-lg border border-gray-200 bg-white text-sm text-gray-900 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-shadow" data-id="element-162" />
        </div>

        <div className="hidden md:block w-px h-6 bg-gray-200 flex-shrink-0" data-id="element-163"></div>

        <div className="flex flex-wrap gap-2 w-full md:w-auto items-center" data-id="element-164">
          <MultiSelectFilter label="Rate" options={RATE_OPTIONS} selected={rateFilters} onChange={setRateFilters} data-id="element-165" />
          <MultiSelectFilter label="Amount" options={AMOUNT_OPTIONS} selected={amountFilters} onChange={setAmountFilters} data-id="element-166" />
          <MultiSelectFilter label="Funded" options={DATE_OPTIONS} selected={dateFilters} onChange={setDateFilters} data-id="element-167" />
          <MultiSelectFilter label="Type" options={TYPE_OPTIONS} selected={loanTypeFilters} onChange={setLoanTypeFilters} data-id="element-168" />

          {hasActiveFilters && <button onClick={clearFilters} className="h-9 text-sm text-gray-500 hover:text-gray-700 flex items-center gap-1.5 px-3 rounded-lg border border-gray-200 hover:bg-gray-50 bg-white transition-colors" data-id="element-169">
              <XIcon className="w-3.5 h-3.5" data-id="element-170" />
              Clear all
            </button>}
        </div>

        <div className="hidden md:block ml-auto text-xs text-gray-500 flex-shrink-0" data-id="element-171">
          Showing {filteredBorrowers.length} of {borrowers.length}
        </div>
      </div>

      <CardContent className="p-0 flex-1" data-id="element-172">
        {filteredBorrowers.length === 0 ? <div className="h-64 flex flex-col items-center justify-center text-center p-8" data-id="element-173">
            <div className="bg-gray-50 p-4 rounded-full mb-4" data-id="element-174">
              <UserPlusIcon className="w-8 h-8 text-gray-400" data-id="element-175" />
            </div>
            <h3 className="text-lg font-medium text-gray-900" data-id="element-176">
              No unclaimed borrowers found
            </h3>
            <p className="text-sm text-gray-600 mt-1 max-w-xs mx-auto" data-id="element-177">
              {borrowers.length === 0 ? 'The inactive owner pool is currently empty.' : 'Try adjusting your filters or search query to see more results.'}
            </p>
            {hasActiveFilters && <Button variant="outline" size="sm" onClick={clearFilters} className="mt-4" data-id="element-178">
                Clear Filters
              </Button>}
          </div> : <div className="overflow-x-auto" data-id="element-179">
            <table className="w-full text-left text-sm" data-id="element-180">
              <thead className="bg-gray-50 border-b border-gray-200" data-id="element-181">
                <tr data-id="element-182">
                  <th className="px-6 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider cursor-pointer hover:text-gray-700 select-none" onClick={() => handleSort('name')} data-id="element-183">
                    Name <SortIcon column="name" data-id="element-184" />
                  </th>
                  <th className="px-3 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider cursor-pointer hover:text-gray-700 select-none" onClick={() => handleSort('phone')} data-id="element-185">
                    Phone <SortIcon column="phone" data-id="element-186" />
                  </th>
                  <th className="px-3 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider cursor-pointer hover:text-gray-700 select-none" onClick={() => handleSort('email')} data-id="element-187">
                    Email <SortIcon column="email" data-id="element-188" />
                  </th>
                  <th className="px-3 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider text-right cursor-pointer hover:text-gray-700 select-none" onClick={() => handleSort('loanAmount')} data-id="element-189">
                    Loan Amount <SortIcon column="loanAmount" data-id="element-190" />
                  </th>
                  <th className="px-3 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider text-center cursor-pointer hover:text-gray-700 select-none" onClick={() => handleSort('loanType')} data-id="element-191">
                    Type <SortIcon column="loanType" data-id="element-192" />
                  </th>
                  <th className="px-3 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider text-center cursor-pointer hover:text-gray-700 select-none" onClick={() => handleSort('rate')} data-id="element-193">
                    Rate <SortIcon column="rate" data-id="element-194" />
                  </th>
                  <th className="px-3 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider text-center cursor-pointer hover:text-gray-700 select-none" onClick={() => handleSort('fundedDate')} data-id="element-195">
                    Funded <SortIcon column="fundedDate" data-id="element-196" />
                  </th>
                  <th className="px-3 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider text-center cursor-pointer hover:text-gray-700 select-none" onClick={() => handleSort('lastContactAttempt')} data-id="element-197">
                    Last Contact <SortIcon column="lastContactAttempt" data-id="element-198" />
                  </th>
                  <th className="px-6 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider text-right" data-id="element-199">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100" data-id="element-200">
                {sortedBorrowers.map(borrower => {
              const isClaiming = claimingId === borrower.id;
              return <tr key={borrower.id} className={`transition-colors duration-300 ${isClaiming ? 'bg-green-50' : !borrower.lastContactAttempt ? 'bg-red-50/40 hover:bg-red-50/70 border-l-2 border-l-red-300' : 'hover:bg-gray-50/80'}`} data-id="element-201">
                      <td className="px-6 py-3.5 align-middle" data-id="element-202">
                        <div className="font-medium text-gray-900 whitespace-nowrap" data-id="element-203">
                          {borrower.name}
                        </div>
                      </td>
                      <td className="px-3 py-3.5 align-middle" data-id="element-204">
                        <span className="flex items-center gap-1.5 text-sm text-gray-600 whitespace-nowrap" data-id="element-205">
                          <PhoneIcon className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" data-id="element-206" />
                          {borrower.phone}
                        </span>
                      </td>
                      <td className="px-3 py-3.5 align-middle" data-id="element-207">
                        <span className="flex items-center gap-1.5 text-sm text-gray-600 whitespace-nowrap" data-id="element-208">
                          <MailIcon className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" data-id="element-209" />
                          {borrower.email}
                        </span>
                      </td>
                      <td className="px-3 py-3.5 align-middle text-right" data-id="element-210">
                        <span className="font-medium text-gray-900 whitespace-nowrap tabular-nums" data-id="element-211">
                          {formatUSD(borrower.loanAmount)}
                        </span>
                      </td>
                      <td className="px-3 py-3.5 align-middle text-center" data-id="element-212">
                        <Badge variant="blue" data-id="element-213">{borrower.loanType}</Badge>
                      </td>
                      <td className="px-3 py-3.5 align-middle text-center" data-id="element-214">
                        <Badge variant={borrower.rate > 7 ? 'warning' : 'neutral'} data-id="element-215">
                          {borrower.rate.toFixed(3)}%
                        </Badge>
                      </td>
                      <td className="px-3 py-3.5 align-middle text-center text-gray-600 whitespace-nowrap tabular-nums" data-id="element-216">
                        {new Date(borrower.fundedDate).toLocaleDateString()}
                      </td>
                      <td className="px-3 py-3.5 align-middle text-center text-gray-600 whitespace-nowrap tabular-nums" data-id="element-217">
                        {borrower.lastContactAttempt ? <div data-id="element-218">
                            <div className="text-gray-900 text-sm" data-id="element-219">
                              {new Date(borrower.lastContactAttempt).toLocaleDateString()}
                            </div>
                            <div className="text-gray-400 text-xs" data-id="element-220">
                              {new Date(borrower.lastContactAttempt).toLocaleTimeString([], {
                        hour: '2-digit',
                        minute: '2-digit'
                      })}
                            </div>
                          </div> : <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-medium bg-red-50 text-red-600 border border-red-200" data-id="element-221">
                            <PhoneIcon className="w-2.5 h-2.5" data-id="element-222" />
                            Never
                          </span>}
                      </td>
                      <td className="px-6 py-3.5 align-middle text-right" data-id="element-223">
                        <div className="flex items-center justify-end" data-id="element-224">
                          {isClaiming ? <div className="flex items-center text-green-600 font-medium text-sm px-3 py-1.5" data-id="element-225">
                              <CheckCircleIcon className="w-4 h-4 mr-1.5" data-id="element-226" />
                              Claimed
                            </div> : <Button size="sm" variant="primary" onClick={() => handleClaim(borrower.id)} className="whitespace-nowrap" data-id="element-227">
                              <UserPlusIcon className="mr-1.5 h-3.5 w-3.5" data-id="element-228" />
                              Claim
                            </Button>}
                        </div>
                      </td>
                    </tr>;
            })}
              </tbody>
            </table>
          </div>}
      </CardContent>
    </Card>;
}